import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/lib/supabaseClient";
import { useSupabaseSession } from "@/lib/SupabaseSessionContext";

/**
 * Realtime online presence via Supabase Presence API.
 *
 * - Subscribes to the global `fp-presence` channel after Supabase session is ready.
 * - Tracks the current user's status (`online` | `lft` | `away`).
 * - Exposes the live set of online user_ids + their status to the whole app.
 * - Heartbeats `online_presence` table every 60s as a DB fallback (used by lists
 *   that need to render online dots without subscribing to the channel).
 *
 * Status values are kept short for wire-payload size:
 *   online → green dot
 *   lft    → "Looking for Team" (purple/neon dot)
 *   away   → idle (gray dot)
 */
const STATUS_VALUES = new Set(["online", "lft", "away"]);

const PresenceCtx = createContext({
  online: new Map(),
  status: "online",
  setStatus: () => {},
  isOnline: () => false,
  statusOf: () => null,
});

export function PresenceProvider({ children }) {
  const { ready, supabaseUserId } = useSupabaseSession();
  const [online, setOnline] = useState(new Map()); // user_id -> { status, ts }
  const [status, setStatusRaw] = useState(() => {
    try { return localStorage.getItem("fp:presence:status") || "online"; }
    catch (_e) { return "online"; }
  });
  const channelRef = useRef(null);
  const heartbeatRef = useRef(null);

  const setStatus = useCallback((v) => {
    if (!STATUS_VALUES.has(v)) return;
    setStatusRaw(v);
    try { localStorage.setItem("fp:presence:status", v); } catch (_e) { /* noop */ }
    // re-track with the new status if channel is live
    if (channelRef.current && supabaseUserId) {
      channelRef.current.track({ user_id: supabaseUserId, status: v });
    }
  }, [supabaseUserId]);

  // Subscribe / unsubscribe
  useEffect(() => {
    if (!ready || !supabase || !supabaseUserId) return;

    const ch = supabase.channel("fp-presence", {
      config: { presence: { key: supabaseUserId } },
    });

    ch.on("presence", { event: "sync" }, () => {
      const state = ch.presenceState();
      const next = new Map();
      Object.values(state).forEach((arr) => {
        arr.forEach((meta) => {
          if (!meta?.user_id) return;
          next.set(meta.user_id, { status: meta.status || "online", ts: Date.now() });
        });
      });
      setOnline(next);
    });

    ch.subscribe(async (s) => {
      if (s !== "SUBSCRIBED") return;
      try {
        await ch.track({ user_id: supabaseUserId, status });
      } catch (_e) { /* noop */ }
    });
    channelRef.current = ch;

    // Heartbeat to online_presence (DB fallback for non-channel consumers)
    const beat = async () => {
      try {
        await supabase.from("online_presence").upsert({
          user_id: supabaseUserId,
          status: status === "lft" ? "online" : status,  // table only knows online|away|offline
          last_seen_at: new Date().toISOString(),
        }, { onConflict: "user_id" });
        await supabase.from("profiles").update({
          is_online: true,
          last_seen_at: new Date().toISOString(),
        }).eq("user_id", supabaseUserId);
      } catch (_e) { /* noop */ }
    };
    beat();
    heartbeatRef.current = setInterval(beat, 60_000);

    // Mark offline on unload (best-effort — browsers throttle this)
    const onBye = () => {
      try {
        supabase.from("profiles").update({ is_online: false }).eq("user_id", supabaseUserId);
        ch.untrack();
      } catch (_e) { /* noop */ }
    };
    window.addEventListener("beforeunload", onBye);

    return () => {
      window.removeEventListener("beforeunload", onBye);
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      try {
        supabase.from("profiles").update({ is_online: false }).eq("user_id", supabaseUserId);
      } catch (_e) { /* noop */ }
      supabase.removeChannel(ch);
      channelRef.current = null;
    };
  }, [ready, supabaseUserId, status]);

  const isOnline = useCallback((uid) => online.has(uid), [online]);
  const statusOf = useCallback((uid) => online.get(uid)?.status || null, [online]);

  return (
    <PresenceCtx.Provider value={{ online, status, setStatus, isOnline, statusOf }}>
      {children}
    </PresenceCtx.Provider>
  );
}

export function usePresence() {
  return useContext(PresenceCtx);
}
